import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { brl } from "@/lib/format";
import { ProductDialog } from "@/components/admin/ProductDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const StockControl = ({ product, onChange }) => {
  const [value, setValue] = useState(product.stock);
  useEffect(() => setValue(product.stock), [product.stock]);
  const btn = "h-8 rounded-md bg-white/5 px-2 text-xs font-semibold hover:bg-white/10";
  return (
    <div className="flex items-center gap-1">
      <button data-testid={`stock-minus10-${product.id}`} className={btn} onClick={() => onChange(product, { delta: -10 })}>-10</button>
      <button data-testid={`stock-minus1-${product.id}`} className={btn} onClick={() => onChange(product, { delta: -1 })}>-1</button>
      <input data-testid={`stock-quantity-input-${product.id}`} type="number" min={0} value={value} onChange={(e) => setValue(e.target.value)}
        onBlur={() => Number(value) !== product.stock && value !== "" && onChange(product, { set: parseInt(value, 10) })}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className={`h-8 w-16 rounded-md border bg-black/30 text-center text-sm outline-none focus:border-primo-500 ${product.stock <= 5 ? "border-red-500/50 text-red-400" : "border-white/10"}`} />
      <button data-testid={`stock-plus1-${product.id}`} className={btn} onClick={() => onChange(product, { delta: 1 })}>+1</button>
      <button data-testid={`stock-plus10-${product.id}`} className={btn} onClick={() => onChange(product, { delta: 10 })}>+10</button>
    </div>
  );
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const changeStock = async (product, body) => {
    try {
      const { data } = await api.patch(`/admin/products/${product.id}/stock`, body);
      setProducts((ps) => ps.map((p) => (p.id === data.id ? data : p)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/products/${toDelete.id}`);
      toast.success("Produto excluído");
      setToDelete(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openDialog = (p) => { setEditing(p); setDialogOpen(true); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">{products.length} produtos</p>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Produtos & Estoque</h1>
        </div>
        <button data-testid="add-product-admin-button" onClick={() => openDialog(null)}
          className="flex items-center gap-2 rounded-full bg-primo-500 px-5 py-2.5 font-semibold text-white hover:bg-primo-400"><Plus className="h-4 w-4" /> Novo produto</button>
      </div>

      <div className="space-y-2">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/[0.08] bg-[#1A1A1A] p-3">
            <img src={p.image_url} alt="" className="h-14 w-14 rounded-xl object-cover bg-black/30" />
            <div className="min-w-[160px] flex-1">
              <p className="font-semibold">{p.name} {!p.active && <span className="ml-1 rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-slate-400">OCULTO</span>}</p>
              <p className="text-sm text-slate-500">{p.category} · <span className="text-primo-400">{brl(p.price)}</span></p>
            </div>
            <StockControl product={p} onChange={changeStock} />
            <div className="flex gap-1">
              <button data-testid={`edit-product-${p.id}`} onClick={() => openDialog(p)} className="grid h-9 w-9 place-items-center rounded-lg bg-white/5 hover:bg-white/10"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`delete-product-${p.id}`} onClick={() => setToDelete(p)} className="grid h-9 w-9 place-items-center rounded-lg bg-white/5 text-red-400 hover:bg-red-500/20"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <ProductDialog open={dialogOpen} onOpenChange={setDialogOpen} product={editing}
        categories={[...new Set(products.map((p) => p.category))]} onSaved={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#1A1A1A]">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir produto?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}" será removido permanentemente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" onClick={remove} className="bg-red-600 text-white hover:bg-red-500">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
